import React, { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import { designTokens } from '@/utils/design-tokens'
import { PerformanceGroupService } from '@/api/generated/services/PerformanceGroupService'
import { PerformanceGroupCreateRequest } from '@/api/generated/models/PerformanceGroupCreateRequest'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import RequiredLabel from '@/components/ui/RequiredLabel'

interface PerformanceIndicatorCreatePanelProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  businessId: string
  onSuccess?: () => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div 
    className="pb-2 border-b" 
    style={{ 
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{ 
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean 
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span 
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const PerformanceIndicatorCreatePanel: React.FC<PerformanceIndicatorCreatePanelProps> = ({
  isOpen,
  onClose,
  workspaceId,
  businessId,
  onSuccess
}) => {
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const { showError } = useAlert()
  
  // Form state
  const [indicatorName, setIndicatorName] = useState('')
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when panel opens/closes
  useEffect(() => {
    if (isOpen) {
      setIndicatorName('')
    }
  }, [isOpen])

  const handleSubmit = async () => {
    if (!workspaceId) {
      showError('워크스페이스가 선택되지 않았습니다.')
      return
    }

    if (!businessId) {
      showError('사업이 선택되지 않았습니다.')
      return
    }

    if (!indicatorName.trim()) {
      showError('지표구분명을 입력해주세요.')
      return
    }


    setIsSubmitting(true)

    try {
      const createRequest: PerformanceGroupCreateRequest = {
        workspace_id: workspaceId,
        business_id: businessId,
        name: indicatorName.trim()
      }

      await PerformanceGroupService.appsPerformanceViewsCreatePerformanceGroup({
        requestBody: createRequest
      })

      // Success
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Failed to create performance indicator:', err)
      
      // Extract error details from response
      const errorData = err?.body || err?.response?.data || err
      const errorMessages = []
      
      // Collect all available error information
      if (errorData?.details) {
        if (Array.isArray(errorData.details)) {
          errorMessages.push(...errorData.details)
        } else if (typeof errorData.details === 'object') {
          // JSON 객체인 경우 문자열로 변환
          errorMessages.push(JSON.stringify(errorData.details))
        } else {
          errorMessages.push(errorData.details)
        }
      }
      
      if (errorData?.message) {
        errorMessages.push(errorData.message)
      }
      
      if (errorData?.error) {
        errorMessages.push(errorData.error)
      }
      
      // If we have specific error messages, use them
      if (errorMessages.length > 0) {
        showError(errorMessages.join(' / '))
      } else {
        // Fallback to status-based messages
        if (err && typeof err === 'object' && 'status' in err) {
          switch (err.status) {
            case 400:
              showError('입력 데이터가 올바르지 않습니다.')
              break
            case 401:
              showError('인증이 만료되었습니다. 다시 로그인해주세요.')
              break
            case 409:
              showError('이미 존재하는 지표구분명입니다.')
              break
            case 500:
              showError('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
              break
            default:
              showError('지표구분 생성에 실패했습니다. 다시 시도해주세요.')
          }
        } else {
          showError('지표구분 생성에 실패했습니다. 다시 시도해주세요.')
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (isSubmitting) return
    onClose()
  }

  const isFormValid = indicatorName.trim()

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSubmit}
        disabled={isSubmitting || !isFormValid}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title="지표구분 생성"
      width={800}
      headerActions={headerActions}
    >
      <div 
        className="flex flex-col space-y-6" 
        style={{ 
          padding: '24px',
        }}
      >

        {/* Section 1: 기본정보 */}
        <div className="space-y-4">
          <SectionHeader title="기본정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField 
              label="지표구분 유형" 
              value={
                <Tag color="info" size="small">
                  ● 지표구분 (PerformanceGroup)
                </Tag>
              }
            />
            <InfoField 
              label="유형" 
              value={
                <Tag color="info" size="small">
                  ● 지표구분
                </Tag>
              }
            />
            <InfoField 
              label="레벨" 
              value="1" 
            />
            <InfoField 
              label="상위 분류" 
              value="-" 
            />
          </div>
        </div>

        {/* Section 2: 지표구분 정보 */}
        <div className="space-y-4">
          <SectionHeader title="지표구분 정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            {/* 지표구분명 입력 */}
            <InfoField
              label={<RequiredLabel>지표구분명</RequiredLabel>}
              fullWidth={true}
              value={
                <input
                  type="text"
                  value={indicatorName}
                  onChange={(e) => setIndicatorName(e.target.value)}
                  placeholder="지표구분명을 입력하세요"
                  className="w-full p-2 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                  disabled={isSubmitting}
                />
              }
            />

          </div>
        </div>
      </div>
    </SidePanel>
  )
}

export default PerformanceIndicatorCreatePanel